import axios from "axios";
import { ip_back } from "../config";
import { syncBrandAfterAuth } from "./brandAuth";
import { applyClubAdminUiLanguage } from "./clubCountryLanguage";
import { HOME_ACTIVE_ROLE_KEY } from "./homeRole";
import { recordPrivacyAndTermsAcceptance } from "../api/socialClipApi";

export function mergeClubAdminUser(user, profiling, clubId) {
  const selected = profiling?.selectedClub || {};
  const club_details = {
    ...(user?.club_details || {}),
    club_id: clubId || user?.club_id || selected.club_id || user?.club_details?.club_id,
    club_name:
      user?.club_details?.club_name ||
      user?.club_name ||
      selected.club_name ||
      profiling?.clubName,
    country:
      user?.club_details?.country ||
      selected.country ||
      selected.club_country ||
      profiling?.clubCountryCode ||
      profiling?.clubCountry,
    club_description:
      user?.club_details?.club_description || selected.club_description,
    club_code: user?.club_details?.club_code || selected.club_code,
    city:
      user?.club_details?.city ||
      selected.city ||
      selected.club_city ||
      profiling?.clubCity,
    postal_code: user?.club_details?.postal_code || selected.postal_code,
  };
  return {
    ...user,
    club_id: club_details.club_id || user?.club_id,
    club_name: club_details.club_name || user?.club_name,
    club_details,
  };
}

function readDualRoleData() {
  try {
    const raw = localStorage.getItem("dualRoleData");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

async function linkAdminToClub(user, profiling, clubId) {
  const isCommittee = profiling?.inviteRole === "committee" || profiling?.userType === "committee";
  const adminData = {
    user_id: user.user_id,
    club_id: clubId,
    admin_description: isCommittee ? "Committee Member" : "Club Administrator",
    adminPosition: profiling.adminPosition,
    clubWebsite: profiling.clubWebsite,
    invitationToken: profiling.invitationToken || undefined,
    adminProofUrl: profiling.adminProofUrl || undefined,
    scforh: profiling.scforh || undefined,
  };
  const adminResponse = await axios.post(`${ip_back}api/club/createAdmin`, adminData);
  if (adminResponse.data.status !== "success") {
    throw new Error(adminResponse.data.message || "Admin relationship creation failed");
  }
  return adminResponse.data.data?.user || user;
}

/**
 * Register a club administrator from DigiPing (or the standalone password page).
 * Does not persist the password. Returns the path to navigate after session is stored.
 */
export async function completeClubAdminRegistration({
  password,
  profiling,
  i18n,
  loadPermissions,
  refreshBrand,
}) {
  const isCommittee = profiling?.inviteRole === "committee" || profiling?.userType === "committee";
  const restoringDeleted = Boolean(profiling?.restoringDeletedAccount);
  const isExistingAccount =
    Boolean(profiling?.isExistingAccount || profiling?.existingAccount) &&
    !restoringDeleted;
  const invitationToken = profiling?.invitationToken || null;
  const isInvited = Boolean(invitationToken);
  const emailConfirmationToken = localStorage.getItem("emailConfirmationToken");
  const emailToUse =
    localStorage.getItem("pendingEmail") || profiling?.adminEmail || profiling?.email;

  if (!emailToUse) {
    throw new Error("Email is required. Please complete the profiling form with your email first.");
  }
  if (!emailConfirmationToken && !isInvited && !isExistingAccount) {
    throw new Error("Email confirmation required. Please start the registration process again.");
  }

  const isDualRole = Boolean(profiling?.isDualRole);
  const parsedDualRoleData = readDualRoleData();
  let newUser;

  if (isExistingAccount) {
    const loginResponse = await axios.post(`${ip_back}api/user/login`, {
      email: emailToUse,
      password,
    });
    if (loginResponse.data?.status !== "success" || !loginResponse.data?.data) {
      throw new Error(loginResponse.data?.message || "Authentication failed");
    }
    newUser = loginResponse.data.data;
  } else if (!isCommittee && (isDualRole || parsedDualRoleData)) {
    let existingUser;
    if (parsedDualRoleData) {
      existingUser = parsedDualRoleData.existingUser;
    } else {
      const existingUserResponse = await axios.post(`${ip_back}api/user/getUserByEmail`, {
        email: emailToUse,
      });
      if (existingUserResponse.data.status !== "success") {
        throw new Error("Could not find existing user for dual role creation");
      }
      existingUser = existingUserResponse.data.data;
    }

    const isExistingAdmin = existingUser.type_user_id === 2;
    const isExistingPlayer = existingUser.type_user_id === 4;

    if (
      (isExistingAdmin && profiling?.userType === "club_admin") ||
      (parsedDualRoleData && parsedDualRoleData.newRole === "player")
    ) {
      const playerData = parsedDualRoleData
        ? { grip: null, hand: null, level_id: null }
        : {
            grip: profiling.playerGrip,
            hand: profiling.playerHand,
            level_id: profiling.levelId,
          };
      const dualRoleResponse = await axios.post(`${ip_back}api/club/createDualRoleAdminPlayer`, {
        user_id: existingUser.user_id,
        club_id: profiling?.selectedClub?.club_id || existingUser.club_id,
        playerData,
      });
      if (dualRoleResponse.data.status !== "success") {
        throw new Error(dualRoleResponse.data.message || "Dual role creation failed");
      }
      newUser = dualRoleResponse.data.data.user;
    } else if (isExistingPlayer && profiling?.userType === "club_admin") {
      newUser = await linkAdminToClub(existingUser, profiling, profiling.selectedClub?.club_id);
    } else {
      throw new Error("Invalid dual role scenario");
    }
  } else {
    const clubIdHint =
      profiling?.selectedClub?.club_id || profiling?.clubId || profiling?.club || null;
    const registerData = {
      email: emailToUse,
      password,
      firstName: profiling?.adminFirstName,
      lastName: profiling?.adminLastName,
      sex: profiling?.adminGender || "male",
      ...(profiling?.adminDateOfBirth ? { dateBorn: new Date(profiling.adminDateOfBirth) } : {}),
      typeUser_id: isCommittee ? 6 : 2,
      confirmation_token: isInvited ? invitationToken : emailConfirmationToken,
      invitation_token: invitationToken || undefined,
      adminPosition: profiling?.adminPosition,
      club_id: clubIdHint,
    };
    const registerResponse = await axios.post(`${ip_back}api/user/registerUser`, registerData);
    if (registerResponse.data.status !== "success") {
      throw new Error(registerResponse.data.message || "Registration failed");
    }
    newUser = registerResponse.data.data;
  }

  let clubId;
  if (profiling.selectedClub && profiling.selectedClub.club_id) {
    clubId = profiling.selectedClub.club_id;
    newUser = await linkAdminToClub(newUser, profiling, clubId);
  } else {
    const clubResponse = await axios.post(`${ip_back}api/club/createClub`, {
      club_name: profiling.clubName,
      club_country: profiling.clubCountryCode || profiling.clubCountry,
      club_city: profiling.clubCity,
      club_address: profiling.clubAddress,
      club_postal_code: profiling.clubPostalCode,
      club_phone: profiling.clubPhone,
      club_website: profiling.clubWebsite,
      club_description: profiling.clubDescription,
      club_founded_year: profiling.clubFoundedYear,
      club_number_of_members: profiling.clubNumberOfMembers,
      club_number_of_tables: profiling.clubNumberOfTables,
      club_facilities: profiling.clubFacilities,
    });
    if (clubResponse.data.status !== "success") {
      throw new Error(clubResponse.data.message || "Club creation failed");
    }
    clubId = clubResponse.data.data.club.club_id;
    newUser = await linkAdminToClub(newUser, profiling, clubId);
  }

  localStorage.removeItem("profilingFormData");
  if (parsedDualRoleData) localStorage.removeItem("dualRoleData");

  const clubName =
    profiling.clubName ||
    profiling.selectedClub?.club_name ||
    newUser?.club_name ||
    newUser?.club_details?.club_name ||
    null;
  newUser = mergeClubAdminUser(
    {
      ...newUser,
      club_id: clubId || newUser?.club_id,
      club_name: clubName,
      admin_position: profiling?.adminPosition || newUser?.admin_position || null,
      hasAdminRole: isCommittee ? false : true,
      hasCommitteeRole: isCommittee,
      hasPlayerRole: Boolean(newUser?.hasPlayerRole),
      type_user_id: newUser?.type_user_id || (isCommittee ? 6 : 2),
      isProfileCompleted: true,
    },
    profiling,
    clubId
  );
  applyClubAdminUiLanguage(i18n, newUser);

  localStorage.setItem("user", JSON.stringify(newUser));
  localStorage.setItem("token", newUser.user_token);
  localStorage.setItem(HOME_ACTIVE_ROLE_KEY, isCommittee ? "committee" : "admin");
  localStorage.removeItem("emailConfirmationToken");
  localStorage.removeItem("pendingEmail");
  await recordPrivacyAndTermsAcceptance();

  if (newUser.permissions) loadPermissions?.(newUser.permissions);

  if (parsedDualRoleData && parsedDualRoleData.newRole === "player") {
    return { user: newUser, clubId, nextPath: "/profiling" };
  }

  await syncBrandAfterAuth(newUser, refreshBrand, { clubIdHint: clubId });
  return { user: newUser, clubId, nextPath: "/Dashboard" };
}
